import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useThemePalette } from '@/config/theme';
import { useScores365Player } from '@/features/players/hooks';
import type {
  Scores365CareerSeason,
  Scores365HighlightBlock,
  Scores365LastMatch,
  Scores365Player,
  Scores365StatKV,
  Scores365Transfer,
  Scores365Trophy,
} from '@/lib/data/provider';
import { formatTransferDate, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

// Caps on the provider arrays so an unexpectedly long payload can't render
// hundreds of nodes into the section (the visible UI never needs more).
const MAX_HIGHLIGHT_BLOCKS = 6;
const MAX_CAREER_ROWS = 12;
const MAX_RECENT_MATCHES = 8;
const MAX_TRANSFERS = 12;
const MAX_TROPHIES = 20;

/** A 6-digit hex (#RRGGBB / RRGGBB) the provider can be trusted to render. */
const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

function safeHex(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!HEX_RE.test(trimmed)) return null;
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

/** A localized ISO date → "11 Jun 2026"; passes through anything unparseable. */
function formatIsoDate(iso: string | null, options: Parameters<typeof formatTransferDate>[1]): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return formatTransferDate(date, options);
}

/** Section heading shared by every sub-block: a small icon + label. */
function BlockTitle({ icon, label }: { icon: keyof typeof Ionicons.glyphMap; label: string }) {
  const palette = useThemePalette();
  return (
    <View className="mb-2.5 flex-row items-center gap-2">
      <Ionicons name={icon} size={15} color={palette.voltBright} />
      <AppText variant="caption" weight="bold" className="uppercase tracking-wide text-soft">
        {label}
      </AppText>
    </View>
  );
}

/** One {name:value} stat as a compact pill. */
function StatChip({ stat }: { stat: Scores365StatKV }) {
  const { arabicNumerals } = useLocale();
  return (
    <View className="rounded-xl bg-surface-2/60 px-3 py-2">
      <AppText variant="body" weight="bold" tabular className="text-ink">
        {localizeDigits(stat.value, arabicNumerals)}
      </AppText>
      <AppText variant="micro" className="mt-0.5 text-muted" numberOfLines={1}>
        {stat.name}
      </AppText>
    </View>
  );
}

/** ── HEADER ── photo + name + age · nationality · position. */
function ProfileHeader({ profile }: { profile: Scores365Player }) {
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const meta = useMemo(() => {
    const parts: string[] = [];
    if (profile.age != null) {
      parts.push(t('player.profile365.yearsOld', { count: profile.age }));
    }
    if (profile.nationality) parts.push(profile.nationality);
    if (profile.position) parts.push(profile.position);
    return parts;
  }, [profile.age, profile.nationality, profile.position, t]);

  return (
    <View className="flex-row items-center gap-4">
      {profile.photo ? (
        <Image
          source={{ uri: profile.photo }}
          style={{ width: 64, height: 64, borderRadius: 32 }}
          contentFit="cover"
          transition={150}
          cachePolicy="memory-disk"
          accessibilityLabel={profile.name ?? ''}
        />
      ) : (
        <TeamCrest uri={null} name={profile.name ?? '?'} size="xl" />
      )}
      <View className="min-w-0 flex-1 gap-1">
        <AppText variant="heading" weight="bold" className="text-ink" numberOfLines={2}>
          {profile.name}
        </AppText>
        {meta.length > 0 ? (
          <AppText variant="caption" className="text-muted" numberOfLines={2}>
            {localizeDigits(meta.join('  ·  '), arabicNumerals)}
          </AppText>
        ) : null}
      </View>
    </View>
  );
}

/** ── HIGHLIGHT STATS ── per-competition headline numbers. */
const HighlightStats = React.memo(function HighlightStats({
  blocks,
}: {
  blocks: Scores365HighlightBlock[];
}) {
  const { t } = useTranslation();
  // Bound the provider array: keep only blocks that carry stats, capped to 6.
  const usable = useMemo(
    () => blocks.filter((block) => block.stats.length > 0).slice(0, MAX_HIGHLIGHT_BLOCKS),
    [blocks],
  );
  if (usable.length === 0) return null;
  return (
    <View>
      <BlockTitle icon="flash-outline" label={t('player.profile365.highlights')} />
      <View className="gap-3">
        {usable.map((block, index) => (
          <View key={`${block.competition ?? 'comp'}-${index}`}>
            {block.competition ? (
              <AppText variant="micro" weight="semibold" className="mb-1.5 text-soft" numberOfLines={1}>
                {block.competition}
              </AppText>
            ) : null}
            <View className="flex-row flex-wrap gap-2">
              {block.stats.map((stat, statIndex) => (
                <StatChip key={`${stat.name}-${statIndex}`} stat={stat} />
              ))}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
});

/** One labelled value within a career row (name on top, value below). */
function CareerCell({ stat }: { stat: Scores365StatKV }) {
  const { arabicNumerals } = useLocale();
  return (
    <View className="min-w-[64px] flex-1 items-center">
      <AppText variant="caption" weight="bold" tabular className="text-ink">
        {localizeDigits(stat.value, arabicNumerals)}
      </AppText>
      <AppText variant="micro" className="mt-0.5 text-center text-muted" numberOfLines={1}>
        {stat.name}
      </AppText>
    </View>
  );
}

/** ── CAREER STATS ── the current season's competition lines as compact rows. */
const CareerStats = React.memo(function CareerStats({
  seasons,
}: {
  seasons: Scores365CareerSeason[];
}) {
  const { t } = useTranslation();
  // The provider orders seasons current-first; render the first season's rows.
  const current = useMemo(() => seasons.find((season) => season.rows.length > 0), [seasons]);
  // Bound the row count so a long competition list can't blow up the section.
  const rows = useMemo(
    () => (current ? current.rows.slice(0, MAX_CAREER_ROWS) : []),
    [current],
  );
  if (!current) return null;
  return (
    <View>
      <BlockTitle icon="stats-chart-outline" label={t('player.profile365.career')} />
      {current.name ? (
        <AppText variant="micro" weight="semibold" className="mb-1.5 text-soft" numberOfLines={1}>
          {current.shortName ?? current.name}
        </AppText>
      ) : null}
      <View className="overflow-hidden rounded-2xl bg-surface-2/40">
        {rows.map((row, index) => (
          <View
            key={`${row.title ?? 'row'}-${index}`}
            className={`px-3 py-2.5${index === 0 ? '' : ' border-t border-line'}`}
          >
            {row.title ? (
              <AppText variant="micro" weight="semibold" className="mb-2 text-soft" numberOfLines={1}>
                {row.title}
              </AppText>
            ) : null}
            <View className="flex-row gap-2">
              {row.stats.map((stat, statIndex) => (
                <CareerCell key={`${stat.name}-${statIndex}`} stat={stat} />
              ))}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
});

/** A rating badge tinted by the 365 ratingColor (falls back to the volt accent). */
function RatingBadge({ rating, color }: { rating: string | null; color: string | null }) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  if (!rating) {
    return (
      <View
        className="items-center justify-center rounded-md bg-surface-2"
        style={{ width: 38, height: 26 }}
      >
        <AppText variant="micro" weight="bold" className="text-muted">
          –
        </AppText>
      </View>
    );
  }
  const bg = safeHex(color) ?? palette.volt;
  return (
    <View
      className="items-center justify-center rounded-md"
      style={{ backgroundColor: bg, width: 38, height: 26 }}
    >
      <AppText weight="bold" tabular style={{ color: palette.onVolt, fontSize: 12 }}>
        {localizeDigits(rating, arabicNumerals)}
      </AppText>
    </View>
  );
}

/** One side of a recent match: crest + name + score. */
function MatchSideRow({
  name,
  crest,
  score,
  bold,
}: {
  name: string | null;
  crest: string | null;
  score: number | null;
  bold: boolean;
}) {
  const { arabicNumerals } = useLocale();
  return (
    <View className="flex-row items-center gap-2">
      <TeamCrest uri={crest} name={name ?? '?'} size="xs" />
      <AppText
        variant="micro"
        weight={bold ? 'bold' : 'medium'}
        className={`min-w-0 flex-1 ${bold ? 'text-ink' : 'text-soft'}`}
        numberOfLines={1}
      >
        {name ?? '—'}
      </AppText>
      <AppText variant="caption" weight="bold" tabular className="text-ink">
        {score != null ? localizeDigits(String(score), arabicNumerals) : '–'}
      </AppText>
    </View>
  );
}

/** ── RECENT MATCHES ── opponent fixtures with the athlete's rating chip. */
const RecentMatches = React.memo(function RecentMatches({
  matches,
}: {
  matches: Scores365LastMatch[];
}) {
  const { t } = useTranslation();
  // Keep playable rows only, capped to the most recent 8.
  const played = useMemo(
    () =>
      matches
        .filter((match) => match.played || match.home.name || match.away.name)
        .slice(0, MAX_RECENT_MATCHES),
    [matches],
  );
  if (played.length === 0) return null;
  return (
    <View>
      <BlockTitle icon="football-outline" label={t('player.profile365.recentMatches')} />
      <View className="gap-2">
        {played.map((match, index) => {
          const homeWon =
            match.home.score != null &&
            match.away.score != null &&
            match.home.score > match.away.score;
          const awayWon =
            match.home.score != null &&
            match.away.score != null &&
            match.away.score > match.home.score;
          return (
            <View
              key={`${match.gameId ?? 'g'}-${index}`}
              className="flex-row items-center gap-3 rounded-2xl bg-surface-2/40 px-3 py-2.5"
            >
              <View className="min-w-0 flex-1 gap-1.5">
                {match.competition ? (
                  <AppText variant="micro" className="text-muted" numberOfLines={1}>
                    {match.competition}
                  </AppText>
                ) : null}
                <MatchSideRow
                  name={match.home.name}
                  crest={match.home.crest}
                  score={match.home.score}
                  bold={homeWon}
                />
                <MatchSideRow
                  name={match.away.name}
                  crest={match.away.crest}
                  score={match.away.score}
                  bold={awayWon}
                />
              </View>
              <RatingBadge rating={match.rating} color={match.ratingColor} />
            </View>
          );
        })}
      </View>
    </View>
  );
});

/** ── TRANSFERS ── a vertical timeline: crest · club · date · type · fee. */
const Transfers = React.memo(function Transfers({
  transfers,
}: {
  transfers: Scores365Transfer[];
}) {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  // Named transfers only, capped to the 12 most recent.
  const usable = useMemo(
    () => transfers.filter((transfer) => transfer.teamName).slice(0, MAX_TRANSFERS),
    [transfers],
  );
  if (usable.length === 0) return null;
  return (
    <View>
      <BlockTitle icon="swap-horizontal-outline" label={t('player.profile365.transfers')} />
      <View className="gap-3">
        {usable.map((transfer, index) => {
          const date = formatIsoDate(transfer.date, numeralOptions);
          const sub = [date, transfer.type].filter(Boolean).join('  ·  ');
          return (
            <View key={`${transfer.teamName}-${index}`} className="flex-row items-center gap-3">
              <View className="items-center">
                <TeamCrest uri={transfer.crest} name={transfer.teamName ?? '?'} size="sm" />
              </View>
              <View className="min-w-0 flex-1">
                <View className="flex-row items-center gap-1.5">
                  <AppText
                    variant="caption"
                    weight={transfer.active ? 'bold' : 'semibold'}
                    className="min-w-0 shrink text-ink"
                    numberOfLines={1}
                  >
                    {transfer.teamName}
                  </AppText>
                  {transfer.active ? (
                    <View className="rounded-full bg-volt/15 px-2 py-0.5">
                      <AppText variant="micro" weight="bold" className="text-volt-bright">
                        {t('player.profile365.current')}
                      </AppText>
                    </View>
                  ) : null}
                </View>
                {sub ? (
                  <AppText variant="micro" className="mt-0.5 text-muted" numberOfLines={1}>
                    {sub}
                  </AppText>
                ) : null}
              </View>
              {transfer.price ? (
                <AppText variant="caption" weight="bold" tabular style={{ color: palette.voltBright }}>
                  {transfer.price}
                </AppText>
              ) : null}
            </View>
          );
        })}
      </View>
    </View>
  );
});

/** ── TROPHIES ── honours list: name + a count badge. */
const Trophies = React.memo(function Trophies({
  trophies,
}: {
  trophies: Scores365Trophy[];
}) {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  // Named honours only, capped to 20.
  const usable = useMemo(
    () => trophies.filter((trophy) => trophy.displayName ?? trophy.name).slice(0, MAX_TROPHIES),
    [trophies],
  );
  if (usable.length === 0) return null;
  return (
    <View>
      <BlockTitle icon="trophy-outline" label={t('player.profile365.trophies')} />
      <View className="gap-2">
        {usable.map((trophy, index) => (
          <View
            key={`${trophy.name ?? 'trophy'}-${index}`}
            className="flex-row items-center gap-3 rounded-2xl bg-surface-2/40 px-3 py-2.5"
          >
            <Ionicons name="trophy" size={16} color={palette.captain} />
            <AppText variant="caption" weight="semibold" className="min-w-0 flex-1 text-ink" numberOfLines={1}>
              {trophy.displayName ?? trophy.name}
            </AppText>
            {trophy.count > 1 ? (
              <View className="min-w-[24px] items-center rounded-full bg-surface-2 px-2 py-0.5">
                <AppText variant="micro" weight="bold" tabular className="text-soft">
                  {localizeDigits(`×${trophy.count}`, arabicNumerals)}
                </AppText>
              </View>
            ) : null}
          </View>
        ))}
      </View>
    </View>
  );
});

/**
 * A RICH 365scores player profile section, mounted on the player page below the
 * native stats. Resolves the display NAME → a 365 athleteId, then fetches the
 * profile. Every sub-block self-hides when its array is empty, and the WHOLE
 * section self-hides when the name doesn't resolve or the profile is null — so on
 * the (common) unmapped player it adds nothing. Subtly credited "via 365Scores".
 */
export function Scores365Profile({ playerName }: { playerName?: string }) {
  const { t } = useTranslation();
  const { profile, isLoading } = useScores365Player(playerName);

  if (isLoading) {
    return (
      <View className="px-4 pt-4">
        <Skeleton className="h-56 w-full" />
      </View>
    );
  }

  // Unmapped player or best-effort fetch came back empty → render nothing.
  if (!profile) return null;

  return (
    <View className="px-4 pt-4">
      <View className="mb-2 flex-row items-center justify-between">
        <AppText variant="heading">{t('player.profile365.title')}</AppText>
        <AppText variant="micro" className="text-muted">
          {t('player.profile365.via')}
        </AppText>
      </View>

      <Card elevated className="gap-5 px-4 py-5">
        <ProfileHeader profile={profile} />

        {profile.shortBio ? (
          <View>
            <BlockTitle icon="person-outline" label={t('player.profile365.bio')} />
            <AppText variant="caption" className="leading-5 text-soft" numberOfLines={4}>
              {profile.shortBio}
            </AppText>
          </View>
        ) : null}

        <HighlightStats blocks={profile.highlightStats} />
        <CareerStats seasons={profile.careerStats} />
        <RecentMatches matches={profile.lastMatches} />
        <Transfers transfers={profile.transfers} />
        <Trophies trophies={profile.trophies} />
      </Card>
    </View>
  );
}
